import { z } from "zod";
import { QualifiedStatus } from "./partner.js";

// ---------------------------------------------------------------------------
// 国税庁の2つの Web-API（縦串9、028、docs/23 §4）。画面から直接は呼ばず、サーバーが呼んで控え（corporate_lookups／
// invoice_registrations）に置く。アプリケーション ID（.env の NTA_APP_ID）が無いあいだは 503 NTA_UNAVAILABLE で、画面はボタンを出さない。
//   法人番号システム Web-API（Ver.4、XML）  … 法人番号 → 商号・所在地・フリガナ／商号 → 候補
//   適格請求書発行事業者公表システム Web-API（Ver.1、JSON） … 登録番号 T＋13桁 → 該当／取消／失効／見つからない
// 利用規約：商用・第三者提供は可。出典を画面に出す（義務。「出典：国税庁 Web-API」＋「？」の窓に NTA_ATTRIBUTION の2文）。短時間の大量アクセスは 403
// ---------------------------------------------------------------------------

export const NTA_MSG = {
  unavailable: "国税庁への照会はまだ使えません（サーバーにアプリケーション ID が設定されていません）",
  corporateNumberInvalid: "法人番号は13桁の数字です",
  corporateNotFound: "その法人番号の法人は見つかりませんでした",
  nameTooShort: "会社名を2文字以上入れてください（「株式会社」と空白は数えません）",
  failed: "国税庁に照会できませんでした。少し待ってからもう一度お試しください",
  /** 国税庁が入力の形で断った（HTTP 400）。待っても直らないので failed と分ける */
  badQuery: "この書き方では国税庁で探せません。名前を短くするか、法人番号（13桁）でお試しください",
  tooMany: "候補が多すぎます。都道府県で絞るか、名前をもう少し長く入れてください",
  regNoInvalid: "登録番号は T＋13桁の数字です（例：T1234567890123）",
} as const;

/**
 * 出典の2文（国税庁 Web-API の利用規約で義務）。国税庁が指定する文言そのまま（法人番号 Web-API 利用規約 第6条・公表サイト「情報取得元の明示」）。言い換えない・要約しない。
 * 画面には NTA_SOURCE_LABEL と「？」だけを出し、「？」の窓にこの2文をそのまま出す（2026-10-13 射場さん、docs/23 §4。部品は apps/web の NtaSource）
 */
export const NTA_ATTRIBUTION_LINES = [
  "このサービスは、国税庁法人番号システムWeb-API機能を利用して取得した情報をもとに作成しているが、サービスの内容は国税庁によって保証されたものではない。",
  "このサービスは、適格請求書発行事業者公表システムWeb-API機能を利用して取得した情報をもとに作成しているが、サービスの内容は国税庁によって保証されたものではない。",
] as const;
/** 出典の2文を1つの文字に（NTA_ATTRIBUTION_LINES をつないだもの） */
export const NTA_ATTRIBUTION = NTA_ATTRIBUTION_LINES.join("");
/** 画面に見せる出典の名前（全文は横の「？」の窓） */
export const NTA_SOURCE_LABEL = "出典：国税庁 Web-API";

/**
 * 法人格の書き方（NFKC で整えたあとの形＝括弧・英字は半角。㈱ は (株) になる）。長いものを先に。
 * 2つ目は法人番号 Web-API の法人種別（301 株式会社…）＝打った法人格と同じ種類の候補を先に並べる。null は種別で並べない
 */
const NTA_LEGAL_FORMS: ReadonlyArray<readonly [string, string | null]> = [
  ["特定非営利活動法人", null], ["一般社団法人", null], ["一般財団法人", null], ["公益社団法人", null], ["公益財団法人", null],
  ["社会福祉法人", null], ["医療法人社団", null], ["医療法人財団", null], ["医療法人", null], ["学校法人", null], ["宗教法人", null],
  ["弁護士法人", null], ["税理士法人", null], ["司法書士法人", null], ["行政書士法人", null], ["監査法人", null], ["農事組合法人", null], ["NPO法人", null],
  ["株式会社", "301"], ["有限会社", "302"], ["合名会社", "303"], ["合資会社", "304"], ["合同会社", "305"],
  ["(株)", "301"], ["(有)", "302"], ["(名)", "303"], ["(資)", "304"], ["(同)", "305"],
  ["(一社)", null], ["(一財)", null], ["(公社)", null], ["(公財)", null], ["(特非)", null], ["(NPO)", null], ["(医)", null], ["(福)", null], ["(学)", null], ["(社)", null], ["(財)", null],
];

/**
 * 国税庁の商号の検索に送る名前に整える（2026-10-13 射場さん、docs/23 §4）。利用者は見やすさのために空白や「（株）」を入れて打つので、送る前にそろえる。
 *   ① 空白（半角・全角・続けて何個でも）を取る
 *   ② 法人格（株式会社・有限会社…・略記の（株）㈱（有）…）をどこにあっても外す＝前方一致は「法人種別を除いた法人名の先頭から」探す決まりで、略記は国税庁が読み替えない
 *   ③ 半角の英数字・記号・半角カナを全角に＝法人番号 Web-API（target=1）は全角文字だけを受け付ける（半角が1つでも HTTP 400・エラーコード 101）
 * kind は打った法人格の法人種別（候補の並べ方に使う）。何も残らなければ query は空
 */
export function ntaNameQuery(input: string): { query: string; kind: string | null } {
  let s = input.normalize("NFKC").replace(/\s+/g, "");
  let kind: string | null = null;
  for (const [w, k] of NTA_LEGAL_FORMS) {
    if (!s.includes(w)) continue;
    if (!kind && k) kind = k;
    s = s.split(w).join("");
  }
  s = s.replace(/[!-~]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0xfee0));
  return { query: s, kind };
}

/** 「会社情報取得」で住所を入れる形（2026-10-13、Board の「会社選択」に合わせた。docs/23 §4） */
export type NtaAddressMode = "registry" | "short";
export const NTA_ADDRESS_MODE_LABEL: Record<NtaAddressMode, string> = { registry: "登記どおり", short: "短く" };

const KANJI_DIGIT: Record<string, number> = { 一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 };
/** 漢数字（一〜九十九）を数に。読めなければ null */
function kanjiNumber(k: string): number | null {
  const m = /^([一二三四五六七八九]?)(十?)([一二三四五六七八九]?)$/.exec(k);
  if (!m || k === "" || (!m[2] && m[3])) return null;
  if (!m[2]) return KANJI_DIGIT[m[1]!]!;
  return (m[1] ? KANJI_DIGIT[m[1]]! : 1) * 10 + (m[3] ? KANJI_DIGIT[m[3]]! : 0);
}
/** 番地の続き（2丁目2番1号・1丁目2番・123番地の4・2-2-1 など）。「号」で終わる（1号3階 の 3 は建物名の側） */
const BANCHI_CHAIN = /\d+(?:丁目|番地の?|番|の|-)(?:\d+(?:丁目|番地の?|番|の|-))*(?:\d+号?)?/;

/**
 * 国税庁の住所（都道府県・市区町村・丁目番地等）を入力欄に入れる形にする。
 *   registry＝登記どおり（大阪府大阪市北区中崎西2丁目2番1号。数字だけ半角に・建物名も住所のまま）
 *   short＝短く（大阪府大阪市北区中崎西2-2-1。丁目・番地・番・号・「の」をハイフンに・漢数字の丁目は数字に・
 *          番地の続きのあとの文字は建物名の欄へ分ける。全角の英数字・記号は半角に）
 * 番地の形が読めなければ短くせず、そのまま返す（建物名は空）
 */
export function ntaAddressOf(c: { pref: string; city: string; street: string }, mode: NtaAddressMode): { address: string; building: string } {
  const head = `${c.pref}${c.city}`;
  if (mode === "registry") return { address: `${head}${c.street}`.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0)), building: "" };
  const street = c.street
    .normalize("NFKC")
    .replace(/([一二三四五六七八九十]+)丁目/g, (m, k: string) => {
      const n = kanjiNumber(k);
      return n === null ? m : `${n}丁目`;
    })
    .replace(/(\d)\s*[‐‑‒–—―−ー]\s*(?=\d)/g, "$1-");
  const m = BANCHI_CHAIN.exec(street);
  if (!m) return { address: `${head}${street}`, building: "" };
  const chain = m[0].replace(/丁目|番地の?|番|の/g, "-").replace(/号/g, "").replace(/-+/g, "-").replace(/-$/, "");
  const building = street.slice(m.index + m[0].length).replace(/^[\s、,・]+/, "").trim();
  return { address: `${head}${street.slice(0, m.index)}${chain}`, building };
}

/** 法人種別コード（法人番号 Web-API の kind） */
export const CORPORATE_KIND_LABEL: Record<string, string> = {
  "101": "国の機関",
  "201": "地方公共団体",
  "301": "株式会社",
  "302": "有限会社",
  "303": "合名会社",
  "304": "合資会社",
  "305": "合同会社",
  "399": "その他の設立登記法人",
  "401": "外国会社等",
  "499": "その他",
};

/** 法人番号 Web-API から引いた1法人（控えの1行と同じ） */
export const CorporateInfo = z.object({
  corporateNumber: z.string(),
  name: z.string(),
  kind: z.string().nullable(),
  kindLabel: z.string(),
  /** 登録があるときだけ（無い法人が多い） */
  furigana: z.string().nullable(),
  /** 7桁（無ければ空） */
  postalCode: z.string(),
  pref: z.string(),
  city: z.string(),
  /** 丁目番地等（国税庁の表記のまま。全角数字） */
  street: z.string(),
  /** 都道府県＋市区町村＋丁目番地等 を1行に、数字は半角に（現場力の住所の欄にそのまま入る形） */
  address: z.string(),
  /** 登記記録が閉鎖されている（合併・解散など） */
  closed: z.boolean(),
  closeDate: z.string().nullable(),
  updateDate: z.string().nullable(),
  fetchedAt: z.string(),
  /** 控えから答えた（国税庁に聞いていない） */
  fromCache: z.boolean(),
});
export type CorporateInfo = z.infer<typeof CorporateInfo>;

/** 商号で探した結果。GET /corporate?name=&pref= */
export const CorporateSearch = z.object({
  query: z.string(),
  /** 国税庁に送った名前（ntaNameQuery で整えたもの。打った名前と違えば画面に「『…』で探しました」） */
  searchedAs: z.string(),
  /** 国税庁が数えた総数（候補は先頭の 30 件まで） */
  count: z.number().int(),
  items: z.array(CorporateInfo),
  truncated: z.boolean(),
});
export type CorporateSearch = z.infer<typeof CorporateSearch>;

/** 登録番号の照会の結果（顧客に保存するのは POST /partners/:id/check-registration） */
export const RegistrationCheck = z.object({
  registrationNo: z.string(),
  status: QualifiedStatus,
  name: z.string().nullable(),
  address: z.string().nullable(),
  registrationDate: z.string().nullable(),
  disposalDate: z.string().nullable(),
  expireDate: z.string().nullable(),
  checkedAt: z.string(),
  fromCache: z.boolean(),
});
export type RegistrationCheck = z.infer<typeof RegistrationCheck>;

/** 都道府県（JIS X 0401 のコード。商号で探すときの絞り込み）は address.ts の PREFECTURES（030 で1か所に） */
